import { Screen, ScreenConfig } from '../core/Screen';
import { APIManager, StateManager, ScreenRouter } from '../../managers';
import { renderUnitPortrait } from '../components/UnitPortrait';
import { renderStrandShowcase, bindStrandShowcase } from '../components/StrandShowcase';
import { PresenceBar } from '../components/PresenceBar';
import { PLAYER_SPRITES, getFullSpriteUrl, getPortraitUrl, getPortraitTuning, CB } from '../../combat/spriteConfig';
import { screenHeaderHTML, renderScreenCurrencies } from '../components/ScreenHeader';
import { formatUnitClass, formatElement } from '../components/GameTerms';
import { elementColor } from '../../utils/elementColors';
import { Tooltip } from '../components/Tooltip';
import { buildUnitTooltip, rarityColor } from '../components/TooltipContent';
import { getItemIconUrl, getAbilityIconUrl } from '../components/ItemIcon';
import { buildHeroBanner, HeroBannerConfig, HeroRewardConfig, attachHeroBannerTooltips } from '../components/HeroBanner';
import { RosterPicker, renderHomePickerCard } from '../components/RosterPicker';
import { CHAPTER_BRIEFINGS, type NpcBriefing, type DialogueLine } from '../../config/briefings';
import { resolveSpeaker, buildPortraitStyle, applyPortraitTuning, getHubBg, playVoiceover, stopVoiceover, collectNpcSpriteKeys, buildNpcStageHTML, getNpcNameplate, variantKeyForSpeaker } from '../components/NpcBriefingModal';
import { isAutoAnchorsEnabled, setAutoAnchorsEnabled } from '../../combat/spriteAnchors.generated';
import type {
  PlayerData,
  CrawlData,
  CampaignData,
  UnitData,
  ConfigData,
  NotificationData,
  CoachData,
} from '../../types';
import { SKILL_SLOT_KEYS } from '../../types';

export class MainMenuScreen extends Screen {
  readonly screenConfig: ScreenConfig = {
    id: 'main-menu',
    name: 'Main Menu',
    persistent: true,
    needsPhaser: false,
  };

  private presenceBar: PresenceBar | null = null;

  private els = {
    partyShowcase: null as HTMLElement | null,
    heroSlot: null as HTMLElement | null,
    secondarySlot: null as HTMLElement | null,
    coachSlot: null as HTMLElement | null,
  };

  // Pending crawl — set after briefing, cleared on cancel or begin
  private pendingCrawl: { crawlId: string; crawlType: string } | null = null;
  private startingCrawl = false;
  // Roster picker state
  private rosterPickerEl: HTMLElement | null = null;
  private swapSlotIndex = -1;
  // Active briefing teardown — stored so onLeave can clean up
  private briefingTeardown: (() => void) | null = null;

  // Carousel state
  private carouselIndex = 0;
  private carouselCount = 0;
  private carouselTimer: ReturnType<typeof setInterval> | null = null;
  private carouselConfigs: LocalHeroConfig[] = [];

  constructor() {
    super({ rootId: 'main-menu-root' });
  }

  createDOM(container: HTMLElement): void {
    // Debug menu visibility: shown on localhost (so devs see it without auth)
    // or for users flagged isAdmin by the server (production admins).
    const isLocalhost = window.location.hostname === 'localhost';
    const showDebug = isLocalhost || !!StateManager.get('isAdmin');

    const root = document.createElement('div');
    root.id = 'main-menu-root';
    root.innerHTML = `
      <div class="main-menu">
        ${screenHeaderHTML('Home')}
        <div class="presence-bar-slot"></div>
        <div class="menu-body">
          <!-- Stage — 16:9 aspect-locked container. Holds bg, party, NPC.
               Covers the viewport (can overflow up to 30%); overflow clipped by .menu-body. -->
          <div class="menu-stage">
            <div class="menu-bg" style="background-image:url('${import.meta.env.BASE_URL}sprites/processed/bg/town_menu.webp${CB}')"></div>

            <!-- Party showcase — bottom-left, on the town stage -->
            <div class="party-showcase"></div>

            <!-- FEATURED HERO — inside the stage so its bottom anchor (17%)
                 lands on the same line as the party feet, regardless of how
                 the stage cover-sizes against the viewport. -->
            <div class="menu-featured">
              <div class="hero-slot"></div>
            </div>
          </div>

          <!-- Secondary chips — small, bottom strip. Holds whatever isn't the hero. -->
          <div class="menu-secondary">
            <div class="secondary-slot"></div>
          </div>

          <!-- One-shot coach tip banner — shown for the first pending tip, dismissable. -->
          <div class="coach-banner-slot"></div>
        </div>
        <div class="menu-debug-toggle" ${showDebug ? '' : 'style="display:none"'}>
          <button class="menu-debug-hamburger" type="button" title="Dev Tools">\u2699</button>
          <div class="menu-debug-menu menu-debug-collapsed">
            <button class="menu-debug-btn menu-grant-item-btn" type="button">+ Item</button>
            <button class="menu-debug-btn menu-grant-unit-btn" type="button">+ Strand</button>
            <button class="menu-debug-btn menu-grant-all-strands-btn" type="button">+ All Strands</button>
            <button class="menu-debug-btn menu-grant-currencies-btn" type="button">+ 10x Currency</button>
            <button class="menu-debug-btn menu-grant-memories-btn" type="button">+ 1 of each Memory</button>
            <button class="menu-debug-btn menu-grant-all-dyes-btn" type="button">+ All Dyes</button>
            <button class="menu-debug-btn menu-refresh-shop-btn" type="button">+1000g &amp; Reset Shop</button>
            <button class="menu-debug-btn menu-grant-skins-btn" type="button">+ All Skins</button>
            <button class="menu-debug-btn menu-grant-unique-btn" type="button">+ Unique</button>
            <button class="menu-debug-btn menu-skip-campaign-btn" type="button">Skip Step</button>
            <button class="menu-debug-btn menu-complete-campaign-btn" type="button">Skip + Loot</button>
            <button class="menu-debug-btn menu-reset-campaign-btn" type="button">Reset Campaign</button>
            <button class="menu-debug-btn menu-reset-account-btn" type="button">Reset Account</button>
            <button class="menu-debug-btn menu-kill-all-btn" type="button">Kill All</button>
            <button class="menu-debug-btn menu-reset-crawl-btn" type="button">Reset Crawl</button>
            <button class="menu-debug-btn menu-reset-briefings-btn" type="button">Reset Briefings</button>
            <button class="menu-debug-btn menu-toggle-anchors-btn" type="button">Anchors: …</button>
            <button class="menu-debug-btn menu-grant-pattern-btn" type="button">+ Pattern</button>
          </div>
        </div>
      </div>
    `;
    container.appendChild(root);
  }

  protected cacheElements(): void {
    this.els = {
      partyShowcase: this.$('.party-showcase'),
      heroSlot: this.$('.hero-slot'),
      secondarySlot: this.$('.secondary-slot'),
      coachSlot: this.$('.coach-banner-slot'),
    };

    // Initialize presence bar
    const slot = this.$('.presence-bar-slot');
    if (slot) {
      this.presenceBar = new PresenceBar();
      this.presenceBar.initialize(slot);
    }
  }

  protected bindEvents(): void {
    this.$('.menu-reset-crawl-btn')?.addEventListener('click', () => this.resetDailyCrawl());
    this.$('.menu-grant-item-btn')?.addEventListener('click', () => this.adminGrant('item'));
    this.$('.menu-grant-unit-btn')?.addEventListener('click', () => this.adminGrant('unit'));
    this.$('.menu-grant-all-strands-btn')?.addEventListener('click', () => this.adminGrant('allStrands'));
    this.$('.menu-grant-currencies-btn')?.addEventListener('click', () => this.adminGrant('currencies'));
    this.$('.menu-grant-memories-btn')?.addEventListener('click', () => this.adminGrant('memories'));
    this.$('.menu-grant-all-dyes-btn')?.addEventListener('click', () => this.adminGrant('allDyes'));
    this.$('.menu-refresh-shop-btn')?.addEventListener('click', () => this.adminRefreshShop());
    this.$('.menu-grant-skins-btn')?.addEventListener('click', () => this.adminGrant('skins'));
    this.$('.menu-grant-unique-btn')?.addEventListener('click', () => this.adminGrant('unique'));
    this.$('.menu-skip-campaign-btn')?.addEventListener('click', () => this.adminSkipCampaignStep());
    this.$('.menu-complete-campaign-btn')?.addEventListener('click', () => this.adminCompleteCampaignStep());
    this.$('.menu-reset-campaign-btn')?.addEventListener('click', () => this.adminResetCampaign());
    this.$('.menu-reset-account-btn')?.addEventListener('click', () => this.adminResetAccount());
    this.$('.menu-kill-all-btn')?.addEventListener('click', () => this.adminKillAll());
    this.$('.menu-reset-briefings-btn')?.addEventListener('click', () => this.adminResetBriefings());
    this.$('.menu-toggle-anchors-btn')?.addEventListener('click', () => this.adminToggleAnchors());
    this.refreshAnchorsButton();
    this.$('.menu-grant-pattern-btn')?.addEventListener('click', () => this.adminGrantPattern());
    this.$('.menu-debug-hamburger')?.addEventListener('click', () => {
      const menu = this.$('.menu-debug-menu');
      if (menu) menu.classList.toggle('menu-debug-collapsed');
    });

    // Admins land with the menu pre-expanded; localhost devs still need to
    // click the hamburger.
    if (StateManager.get('isAdmin')) {
      this.$('.menu-debug-menu')?.classList.remove('menu-debug-collapsed');
    }
  }

  protected setupSubscriptions(): void {
    this.subscribeState('playerData', () => this.onRender());
    this.subscribeState('unitData', () => this.onRender());
    this.subscribeState('crawlData', () => this.onRender());
    this.subscribeState('eventCrawlData', () => this.onRender());
    this.subscribeState('campaignData', () => this.onRender());
    this.subscribeState('configData', () => this.onRender());
    this.subscribeState('notificationData', () => this.renderParty());
    this.subscribeState('coachData', () => this.renderCoachBanner());
  }

  onEnter(): void {
    this.onRender();
    if (this.carouselCount > 1) this.startCarousel();
  }

  onLeave(): void {
    this.stopCarousel();
    // Clean up any active NPC briefing so stale elements don't linger
    if (this.briefingTeardown) {
      this.briefingTeardown();
    }
  }

  protected onRender(): void {
    this.renderHeader();
    this.renderHubBackground();
    // Skip full re-render during an active briefing — replacing party DOM
    // destroys .nb-speaking highlights and hero slot rebuild is pointless
    // while the featured area is hidden.
    if (!this.briefingTeardown) {
      this.renderParty();
      this.renderHero();
      this.renderSecondary();
      this.renderCoachBanner();
    }
  }

  private renderCoachBanner(): void {
    if (!this.els.coachSlot) return;
    // Hide during briefings — coach nudges would fight for attention with the NPC.
    if (this.briefingTeardown) { this.els.coachSlot.innerHTML = ''; return; }

    const coach = StateManager.get('coachData') as CoachData | null;
    const pending = coach?.pending ?? [];
    if (pending.length === 0) { this.els.coachSlot.innerHTML = ''; return; }

    const tip = coach!.tips[pending[0]];
    if (!tip) { this.els.coachSlot.innerHTML = ''; return; }

    const targetLabel = tip.target === 'party' ? 'Party'
      : tip.target === 'craft' ? 'Craft'
      : tip.target === 'inventory' ? 'Inventory'
      : tip.target === 'soulloom' ? 'Soulloom'
      : null;

    this.els.coachSlot.innerHTML = `
      <div class="coach-banner" data-tip-id="${tip.id}">
        <div class="coach-banner-body">
          <div class="coach-banner-title">${tip.title}</div>
          <div class="coach-banner-text">${tip.text}</div>
        </div>
        <div class="coach-banner-actions">
          ${targetLabel ? `<button class="coach-banner-go" type="button">Go to ${targetLabel}</button>` : ''}
          <button class="coach-banner-dismiss" type="button" aria-label="Dismiss">✕</button>
        </div>
      </div>
    `;

    const bannerEl = this.els.coachSlot.querySelector('.coach-banner') as HTMLElement | null;
    bannerEl?.querySelector('.coach-banner-dismiss')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.dismissCoachTip(tip.id);
    });
    bannerEl?.querySelector('.coach-banner-go')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.dismissCoachTip(tip.id);
      if (tip.target === 'party') {
        const focusUnitId = this.pickPartyFocusForTip(tip.id);
        ScreenRouter.navigate('party', focusUnitId ? { focusUnitId } : undefined);
      }
      else if (tip.target === 'craft') ScreenRouter.navigate('crafting');
      else if (tip.target === 'soulloom') ScreenRouter.navigate('soulloom');
    });
  }

  /** Pick the strand the player most likely wants focused when jumping to Party
   *  from a coach tip. The tip text references "this strand" but the queue
   *  only stores the tip id, so we infer the target from current state. */
  private pickPartyFocusForTip(tipId: string): string | undefined {
    const unitData = StateManager.get('unitData') as UnitData | undefined;
    const roster = unitData?.roster ?? [];
    const party = unitData?.party ?? [];
    // Prefer party slot order so the focused strand is one the player is using.
    const ordered = [...party, ...roster.filter(r => !party.find(p => p.id === r.id))];

    if (tipId === 'skill_slot_unlocked') {
      // Find a strand with an unlocked skill that isn't sitting in any of its skill slots.
      const found = ordered.find(u => {
        const equipped = new Set(SKILL_SLOT_KEYS.map(key => u[key]).filter(Boolean) as string[]);
        return (u.unlockedSkills ?? []).some(s => !equipped.has(s.id));
      });
      if (found) return found.id;
    }
    if (tipId === 'mastery_points') {
      const found = ordered.find(u => (u.unspentMasteryPoints ?? 0) > 0);
      if (found) return found.id;
    }
    return undefined;
  }

  private async dismissCoachTip(tipId: string): Promise<void> {
    // Optimistic: remove locally so the banner closes instantly, then refresh.
    const coach = StateManager.get('coachData') as CoachData | null;
    if (coach) {
      StateManager.set('coachData', {
        ...coach,
        pending: coach.pending.filter(id => id !== tipId),
      });
    }
    await APIManager.dismissCoachTip(tipId);
  }

  private renderHubBackground(): void {
    if (!this.rootEl) return;
    const bgEl = this.rootEl.querySelector('.menu-bg') as HTMLElement | null;
    if (!bgEl) return;

    const player = StateManager.get('playerData') as PlayerData | undefined;
    const campaignData = StateManager.get('campaignData') as CampaignData | undefined;
    const campaignDone = campaignData?.steps?.every(s => s.status === 'completed') ?? false;
    const step = player?.campaignStep ?? 0;
    // Walk back to the most recent chapter that defined a hubBg — keeps the bg
    // stable for chapters that reuse the prior hub (e.g. ch 3 reuses ch 2's Thornhaven).
    // Once the campaign is fully complete, fall back to Chapter 1's hub bg.
    let hubBg: string | undefined;
    if (campaignDone) {
      hubBg = CHAPTER_BRIEFINGS[0]?.hubBg;
    } else {
      for (let i = step; i >= 0; i--) {
        const briefing = CHAPTER_BRIEFINGS[i];
        if (briefing?.hubBg) { hubBg = briefing.hubBg; break; }
      }
    }
    const url = getHubBg(hubBg);
    const expected = `url("${url}")`;
    if (bgEl.style.backgroundImage !== expected) {
      bgEl.style.backgroundImage = expected;
    }
  }

  private renderHeader(): void {
    if (!this.rootEl) return;
    const titleEl = this.rootEl.querySelector('.screen-title');
    if (titleEl && !titleEl.querySelector('.menu-logo')) {
      titleEl.innerHTML = `<img src="${import.meta.env.BASE_URL}sprites/processed/images/fatewoven_logo_sm.webp${CB}" alt="Fatewoven" class="menu-logo" />`;
    }
    renderScreenCurrencies(this.rootEl, 'home');

    // Mini currency display (gold + soulthread) below the logo
    const player = StateManager.get('playerData') as PlayerData | undefined;
    let miniCurrency = this.rootEl.querySelector('.menu-mini-currency') as HTMLElement | null;
    if (!miniCurrency) {
      miniCurrency = document.createElement('div');
      miniCurrency.className = 'menu-mini-currency';
      const header = this.rootEl.querySelector('.screen-header');
      if (header) header.appendChild(miniCurrency);
    }
    if (player) {
      const gold = player.gold?.toLocaleString() ?? '0';
      const soulthread = player.soulthread?.toLocaleString() ?? '0';
      miniCurrency.innerHTML = `<span class="mmc-item mmc-gold">${gold}g</span><span class="mmc-sep">\u00B7</span><span class="mmc-item mmc-soul">${soulthread} ST</span>`;
    }

  }

  private renderParty(): void {
    const unitData = StateManager.get('unitData') as UnitData | undefined;
    const configData = StateManager.get('configData') as ConfigData | undefined;
    const notifData = StateManager.get('notificationData') as NotificationData | null;
    const strandAlerts = notifData?.strandAlerts ?? {};
    if (!this.els.partyShowcase || !unitData) return;

    const party = unitData.party ?? [];

    this.els.partyShowcase.innerHTML = party.map((unit) =>
      renderStrandShowcase({
        unit,
        configData,
        swappable: true,
        hasAlert: !!strandAlerts[unit.id],
      })
    ).join('');

    // Bind tap on party members — open roster picker to swap (disabled during briefing)
    this.els.partyShowcase.querySelectorAll<HTMLElement>('.party-member--tappable').forEach((el, idx) => {
      bindStrandShowcase(el, {
        onSwap: () => {
          if (this.rootEl?.querySelector('.main-menu.nb-active') || this.rosterPickerEl) return;
          this.showRosterPicker(idx);
        },
      });
    });
  }

  // ============================================================
  // HERO CAROUSEL — auto-rotating banner cards for all active content.
  // Order: campaign first (if active), event, daily.
  // Each gets a full-size hero banner. Dots indicate count + position.
  // ============================================================

  private renderHero(): void {
    const slot = this.els.heroSlot;
    if (!slot) return;
    const configs = this.pickAllHeroConfigs();
    this.carouselConfigs = configs;

    if (configs.length === 0) {
      slot.innerHTML = '';
      slot.style.display = 'none';
      this.stopCarousel();
      return;
    }
    slot.style.display = '';

    // Clamp carousel index to valid range (content may have changed)
    if (this.carouselIndex >= configs.length) this.carouselIndex = 0;
    this.carouselCount = configs.length;

    // Build slides
    const slidesHTML = configs.map((config, i) => {
      const active = i === this.carouselIndex;
      return `<div class="hero-carousel-slide ${active ? 'active' : ''}" data-slide="${i}">${buildHeroBanner(config)}</div>`;
    }).join('');

    // Dot indicators (only show if >1 slide)
    const dotsHTML = configs.length > 1
      ? `<div class="hero-carousel-dots">${configs.map((_, i) =>
          `<button class="hero-carousel-dot ${i === this.carouselIndex ? 'active' : ''}" data-dot="${i}" type="button"></button>`
        ).join('')}</div>`
      : '';

    slot.innerHTML = `<div class="hero-carousel">${slidesHTML}${dotsHTML}</div>`;

    // Bind CTA buttons + element-badge weakness/resist tooltips on all slides
    configs.forEach((config, i) => {
      const slideEl = slot.querySelector(`[data-slide="${i}"]`) as HTMLElement | null;
      const btn = slideEl?.querySelector('.hero-cta-btn') as HTMLElement | null;
      if (btn && config.onCta) {
        btn.addEventListener('click', () => config.onCta!());
      }
      attachHeroBannerTooltips(slideEl);
    });

    // Bind dot clicks
    slot.querySelectorAll('.hero-carousel-dot').forEach(dot => {
      dot.addEventListener('click', (e) => {
        const idx = parseInt((e.currentTarget as HTMLElement).dataset.dot ?? '0', 10);
        this.goToSlide(idx);
        this.restartCarousel();
      });
    });

    // Pause carousel on hover, resume on leave
    const carouselEl = slot.querySelector('.hero-carousel') as HTMLElement | null;
    if (carouselEl) {
      carouselEl.addEventListener('mouseenter', () => this.stopCarousel());
      carouselEl.addEventListener('mouseleave', () => {
        if (this.carouselCount > 1) this.startCarousel();
      });
    }

    // Start auto-rotation if multiple slides (restart to reset timer)
    this.stopCarousel();
    if (configs.length > 1) {
      this.startCarousel();
    }
  }

  private renderSecondary(): void {
    // Carousel handles all content — secondary chips only for edge cases
    const slot = this.els.secondarySlot;
    if (!slot) return;
    slot.innerHTML = '';
    slot.style.display = 'none';
  }

  /** Build all eligible hero configs in display order. */
  private pickAllHeroConfigs(): LocalHeroConfig[] {
    const player = StateManager.get('playerData') as PlayerData | undefined;
    const step = player?.campaignStep ?? 0;
    const campaignData = StateManager.get('campaignData') as CampaignData | undefined;
    const eventData = StateManager.get('eventCrawlData') as CrawlData | undefined;
    const dailyData = StateManager.get('crawlData') as CrawlData | undefined;
    const configData = StateManager.get('configData') as ConfigData | undefined;

    const campaignDone = campaignData?.steps?.every(s => s.status === 'completed') ?? false;
    // BETA LOCKDOWN: dailies + events disabled to prevent power inflation before Act 4 ships.
    // When Act 4 ships: restore `eventActive = !!eventData && step >= 1 && !this.isEventExpired(eventData);`
    // and re-enable `dailyUnlocked` (was always false in alpha — decide separately if dailies turn on).
    const eventActive = false;
    const dailyUnlocked = false;

    const configs: LocalHeroConfig[] = [];

    // Campaign first (primary progression funnel) — returns null when NPC briefing is showing
    if (campaignData) {
      const cfg = campaignDone
        ? this.buildStoryCompleteHeroConfig(campaignData)
        : this.buildCampaignHeroConfig(campaignData);
      if (cfg) configs.push(cfg);
    }
    // Event second (time-limited = high urgency)
    if (eventActive && eventData) {
      configs.push(this.buildEventHeroConfig(eventData, configData));
    }
    // Daily last
    if (dailyUnlocked && dailyData) {
      configs.push(this.buildDailyHeroConfig(dailyData));
    }

    return configs;
  }

  // ── Carousel controls ──

  private goToSlide(index: number): void {
    if (index === this.carouselIndex || index < 0 || index >= this.carouselCount) return;
    const slot = this.els.heroSlot;
    if (!slot) return;

    // Fade out current, fade in target
    const slides = slot.querySelectorAll('.hero-carousel-slide');
    const dots = slot.querySelectorAll('.hero-carousel-dot');

    slides.forEach((s, i) => s.classList.toggle('active', i === index));
    dots.forEach((d, i) => d.classList.toggle('active', i === index));

    this.carouselIndex = index;
  }

  private advanceCarousel(): void {
    const next = (this.carouselIndex + 1) % this.carouselCount;
    this.goToSlide(next);
  }

  private startCarousel(): void {
    if (this.carouselTimer) return;
    this.carouselTimer = setInterval(() => this.advanceCarousel(), 8000);
  }

  private stopCarousel(): void {
    if (this.carouselTimer) {
      clearInterval(this.carouselTimer);
      this.carouselTimer = null;
    }
  }

  private restartCarousel(): void {
    this.stopCarousel();
    this.startCarousel();
  }

  // ----------------------------------------------------------------
  // Config builders — turn raw state into HeroBannerConfig / chip data
  // ----------------------------------------------------------------

  private buildEventHeroConfig(crawlData: CrawlData, configData: ConfigData | undefined): LocalHeroConfig {
    const zoneName = crawlData.config.name ?? 'Event Crawl';
    const roomCount = crawlData.config.encounters?.length ?? 0;
    const isCompleted = crawlData.run?.status === 'completed';
    const isInProgress = crawlData.run?.status === 'in_progress';

    // Featured art: prefer the bundle's homeSet (the canonical "go win it" pair),
    // falling back to crawlConfig.featuredSkinId for crawls without a bundle.
    const homeSet = crawlData.activeEvent?.homeSet;
    const featuredSkinId = homeSet?.skinId ?? crawlData.config.featuredSkinId;
    const featuredSkin = featuredSkinId ? configData?.skins?.[featuredSkinId] : undefined;
    const unitPool = crawlData.config.unitDropPool ?? [];
    const homeUnitClass = homeSet?.unitClass ?? unitPool[0];
    const featuredTemplate = homeUnitClass ? configData?.unitTemplates?.[homeUnitClass] : undefined;

    let spriteUrl = '';
    let spriteName = '';
    if (featuredSkin) {
      spriteUrl = getFullSpriteUrl(featuredSkin.spriteKey);
      spriteName = featuredSkin.name;
    } else if (featuredTemplate) {
      spriteUrl = getFullSpriteUrl(featuredTemplate.spriteKey);
      spriteName = featuredTemplate.name;
    }

    // Reward chips. The featured strand IS the hero sprite, so we don't show
    // it again as a chip — that would be redundant. Skin + unique only.
    const rewards: HeroRewardConfig[] = [];
    if (featuredSkin) {
      rewards.push({
        type: 'skin',
        iconUrl: getPortraitUrl(featuredSkin.spriteKey),
        typeLabel: 'Skin',
        name: featuredSkin.name,
      });
    }
    const guaranteed = crawlData.config.guaranteedCampaignDrops;
    if (guaranteed?.itemDisplayName) {
      const itemBaseRaw = guaranteed.itemBaseId;
      const itemBase = Array.isArray(itemBaseRaw) ? itemBaseRaw[0] : itemBaseRaw;
      rewards.push({
        type: 'unique',
        iconUrl: itemBase ? getItemIconUrl(itemBase) : '',
        typeLabel: 'Unique',
        name: guaranteed.itemDisplayName,
      });
    }

    // Active event bundle — gives shared theme + accent color.
    // Subtitle from the bundle is used in the shop screen but NOT shown on the
    // home card (the bundle subtitles read like cheesy taglines and crowd the layout).
    const activeEvent = crawlData.activeEvent;
    const accentColor = activeEvent?.accentColor ?? '#a06bd0';
    const bundleLabel = activeEvent?.name;

    // CTA
    const ctaState: LocalHeroConfig['ctaState'] = isCompleted
      ? 'completed'
      : isInProgress
        ? 'in_progress'
        : 'available';
    const ctaLabel = isInProgress ? 'Resume' : 'Enter';

    return {
      variant: 'event',
      accentColor,
      bgKey: crawlData.config.bgKey,
      spriteUrl,
      spriteName,
      prebannerLabel: '\u2605 Limited Event \u2605',
      bundleLabel,
      title: zoneName,
      meta: `${roomCount} Rooms \u00B7 Lv.${crawlData.config.dropItemLevel ?? '?'}+`,
      rewards,
      expiryHTML: this.buildExpiryHTML(crawlData),
      ctaLabel,
      ctaState,
      onCta: isCompleted ? undefined : () => {
        if (isInProgress) {
          this.startEventCrawl();
        } else {
          ScreenRouter.navigate('crawl-detail', { crawlId: crawlData.config.id, crawlType: 'event' });
        }
      },
    };
  }

  // BETA LOCKDOWN: shown when all current chapters are cleared.
  // When Act 4 ships: update the title/subtitle to reference the new last act + next chapter,
  // or delete this method entirely once the campaign has a real ending screen.
  private buildStoryCompleteHeroConfig(campaignData: CampaignData): LocalHeroConfig {
    const lastStep = campaignData.steps[campaignData.steps.length - 1];
    return {
      variant: 'campaign',
      accentColor: '#d4a843',
      bgKey: lastStep?.bgKey,
      spriteUrl: '',
      spriteName: '',
      prebannerLabel: '★ Story Complete ★',
      title: 'Act III · The Pale Watch',
      meta: 'Chapter 16 arrives with Act IV',
      rewards: [],
      ctaLabel: 'Standby',
      ctaState: 'completed',
    };
  }

  private buildCampaignHeroConfig(campaignData: CampaignData): LocalHeroConfig | null {
    // Act boundaries — must match the campaign step count per act in
    // src/config/campaign.ts. Acts are 5 chapters each: Act I = 0-4,
    // Act II = 5-9, Act III = 10-14.
    const ACT_1_END = 5;
    const ACT_2_END = 10;
    const totalSteps = campaignData.steps.length;
    const completedSteps = campaignData.steps.filter(s => s.status === 'completed').length;
    const allComplete = completedSteps >= totalSteps;

    const currentStepIndex = campaignData.campaignStep ?? completedSteps;
    let actLabel: string;
    let actSubtitle: string;
    if (currentStepIndex >= ACT_2_END) {
      actLabel = 'Act III';
      actSubtitle = 'The Pale Watch';
    } else if (currentStepIndex >= ACT_1_END) {
      actLabel = 'Act II';
      actSubtitle = 'The Quiet Trade';
    } else {
      actLabel = 'Act I';
      actSubtitle = 'The Flight';
    }
    const currentStep = campaignData.steps.find(s => s.status === 'available');

    // Use the next available step's bg as the backdrop
    const bgKey = currentStep?.bgKey;

    // Show the next chapter's iconic enemy as the hero art (computed server-side
    // via pickIconicEnemySpriteKey — picks the most-frequent enemy across all
    // encounters, so skarn cave shows a skarn rather than the boss).
    // The party is already on screen so we don't repeat it here.
    let spriteUrl = '';
    let spriteName = '';
    if (currentStep?.previewSpriteKey) {
      spriteUrl = getFullSpriteUrl(currentStep.previewSpriteKey);
      spriteName = currentStep.title;
    }

    const ctaState: LocalHeroConfig['ctaState'] = allComplete ? 'completed' : 'available';
    const hasInProgress = campaignData.inProgressRun !== null;
    const isBriefed = this.pendingCrawl?.crawlId === currentStep?.crawlId;
    const briefingData = CHAPTER_BRIEFINGS[currentStepIndex];
    const briefingSeenInStorage = !!currentStep?.crawlId &&
      localStorage.getItem(`briefing_seen_${currentStep.crawlId}`) === '1';
    const hasBriefing = !!briefingData && !isBriefed && !hasInProgress && !briefingSeenInStorage;

    // Before briefing: no hero card — show the NPC on the hub instead
    if (hasBriefing && !allComplete) {
      // Only add the NPC if not already on screen
      if (!this.rootEl?.querySelector('.quest-npc')) {
        this.showQuestNpc(briefingData, currentStep?.title ?? actSubtitle, currentStep?.crawlId, currentStepIndex);
      }
      return null;
    }
    // Remove quest NPC if briefing is done (e.g. after pendingCrawl set)
    this.rootEl?.querySelector('.quest-npc')?.remove();

    const ctaLabel = hasInProgress ? 'Continue' : (completedSteps > 0 ? 'Continue' : 'Begin');

    return {
      variant: 'campaign',
      accentColor: '#d4a843',
      bgKey,
      spriteUrl,
      spriteName,
      prebannerLabel: `★ ${actLabel} · ${actSubtitle} ★`,
      title: currentStep ? currentStep.title : actSubtitle,
      meta: currentStep ? `${currentStep.encounterCount} Rooms \u00B7 Lv.${currentStep.dropItemLevel ?? '?'}+` : undefined,
      elements: currentStep?.previewElements,
      rewards: [],
      ctaLabel,
      ctaState,
      onCta: allComplete ? undefined : () => {
        if (!currentStep?.crawlId) {
          ScreenRouter.navigate('crawls');
          return;
        }
        if (this.pendingCrawl?.crawlId !== currentStep.crawlId) {
          this.pendingCrawl = { crawlId: currentStep.crawlId, crawlType: 'campaign' };
        }
        this.beginCrawl();
      },
    };
  }

  /**
   * Hide the campaign card, show the NPC on the backdrop with a bottom-center
   * dialogue box featuring portrait, typewriter text, page dots, and speaker dimming.
   */
  private showBriefingInline(
    briefing: NpcBriefing,
    _chapterTitle: string,
    crawlId?: string,
    questNpcEl?: HTMLElement,
    stepIndex?: number,
  ): void {
    if (!this.rootEl) return;
    // Tear down any active briefing before starting a new one
    if (this.briefingTeardown) {
      this.briefingTeardown();
    }
    this.stopCarousel();

    // Filter by requiredCondition but preserve the original index so voiceover
    // filenames stay stable when conditional lines are toggled in/out.
    const party = (StateManager.get('unitData') as { party?: Array<{ unitClass: string; race: string }> } | undefined)?.party ?? [];
    const matchCondition = (u: { unitClass: string; race: string }, cond: NonNullable<DialogueLine['requiredCondition']>): boolean => {
      if (cond.templateId && u.unitClass !== cond.templateId) return false;
      if (cond.race && u.race !== cond.race) return false;
      if (cond.className && !u.unitClass.includes(cond.className)) return false;
      return true;
    };
    const dialogueWithIdx = briefing.dialogue
      .map((line, originalIdx) => ({ line, originalIdx }))
      .filter(({ line }) => !line.requiredCondition || party.some(u => matchCondition(u, line.requiredCondition!)));
    if (!dialogueWithIdx.length) return;
    const dialogue = dialogueWithIdx.map(d => d.line);
    const originalIndices = dialogueWithIdx.map(d => d.originalIdx);
    const audioBase = stepIndex != null ? `ch${stepIndex}_briefing` : null;
    let pageIndex = 0;

    // Typewriter state
    const SPEED = 18;
    let twTimer: number | null = null;
    let twTyping = false;
    let twFull = '';

    // Hide carousel and secondary chips
    const featured = this.rootEl.querySelector('.menu-featured') as HTMLElement | null;
    const secondary = this.rootEl.querySelector('.menu-secondary') as HTMLElement | null;
    if (featured) featured.style.display = 'none';
    if (secondary) secondary.style.display = 'none';

    // Remove any previous briefing elements (stale from interrupted sessions)
    this.rootEl.querySelector('.nb-npc')?.remove();
    this.rootEl.querySelector('.nb-dialogue')?.remove();

    const mainMenu = this.rootEl.querySelector('.main-menu') as HTMLElement;

    // Resolve first line
    const first = resolveSpeaker(briefing, dialogue[0]);
    const firstKey = first.speaker.spriteKey || briefing.spriteKey || '';
    const firstPortraitSrc = getPortraitUrl(firstKey);
    const firstTuning = getPortraitTuning(firstKey);

    // Quest NPC + companion sprites are already on stage from showQuestNpc —
    // we reuse them so positions don't shift when the briefing starts. If
    // there's no quest NPC (called directly without a hub click), build a
    // fresh fallback stage.
    let npcEl: HTMLElement;
    const reuseQuestNpc = !!questNpcEl;

    if (reuseQuestNpc) {
      npcEl = questNpcEl!;
      // Hide the "!" marker and name label (use visibility to preserve layout)
      const marker = questNpcEl!.querySelector('.quest-npc-marker') as HTMLElement | null;
      const nameLabel = questNpcEl!.querySelector('.quest-npc-name') as HTMLElement | null;
      if (marker) marker.style.visibility = 'hidden';
      if (nameLabel) nameLabel.style.visibility = 'hidden';
      questNpcEl!.style.pointerEvents = 'none';
    } else {
      const npcKeys = collectNpcSpriteKeys(briefing, dialogue);
      npcEl = document.createElement('div');
      npcEl.className = npcKeys.length >= 2 ? 'nb-npc nb-npc--multi' : 'nb-npc';
      npcEl.innerHTML = buildNpcStageHTML(
        npcKeys.length ? npcKeys : [briefing.spriteKey].filter(Boolean) as string[],
        firstKey ?? '',
      );
      const stageOrBody = this.rootEl.querySelector('.menu-stage') ?? this.rootEl.querySelector('.menu-body')!;
      stageOrBody.appendChild(npcEl);
    }

    // Dialogue box
    const dialogueEl = document.createElement('div');
    dialogueEl.className = 'nb-dialogue';
    dialogueEl.innerHTML = `
      <div class="nb-header">
        <div class="nb-portrait">
          <img class="nb-portrait-img" src="${firstPortraitSrc}" alt="" draggable="false"
               style="${buildPortraitStyle(firstTuning)}" />
        </div>
        <div class="nb-speaker-info">
          <span class="nb-name"></span>
          <span class="nb-title"></span>
        </div>
      </div>
      <div class="nb-body">
        <div class="nb-text"></div>
      </div>
      <div class="nb-footer">
        <span class="nb-continue">${dialogue.length === 1 ? 'Tap to close &#9656;' : 'Tap to continue &#9656;'}</span>
      </div>
    `;
    mainMenu.appendChild(dialogueEl);

    // Cache elements
    const nameEl = dialogueEl.querySelector('.nb-name') as HTMLElement;
    const titleEl = dialogueEl.querySelector('.nb-title') as HTMLElement;
    const textEl = dialogueEl.querySelector('.nb-text') as HTMLElement;
    const portraitImg = dialogueEl.querySelector('.nb-portrait-img') as HTMLImageElement;
    const continueEl = dialogueEl.querySelector('.nb-continue') as HTMLElement;

    // Set initial speaker info via textContent (safe)
    nameEl.textContent = first.speaker.name;
    nameEl.classList.toggle('nb-name--party', first.speaker.isParty);
    titleEl.textContent = first.speaker.title;
    titleEl.style.display = first.speaker.title ? '' : 'none';

    // Scene state — add nb-active (hides party nameplates, softens vignette)
    mainMenu.classList.add('nb-active');
    mainMenu.classList.toggle('nb-speaker-npc', !first.speaker.isParty);
    mainMenu.classList.toggle('nb-speaker-party', first.speaker.isParty);

    // Ensure the first NPC speaker is the one marked .is-speaking — reopening
    // a briefing after mid-conversation dismissal could leave the flag on a
    // companion sprite.
    if (!first.speaker.isParty && firstKey) {
      const stage = npcEl.parentElement ?? npcEl;
      stage.querySelectorAll('.nb-npc-sprite').forEach(el => el.classList.remove('is-speaking'));
      stage.querySelector(`.nb-npc-sprite[data-sprite-key="${firstKey}"]`)?.classList.add('is-speaking');
    }

    // Dialogue box alignment: NPC → lean right, party → lean left
    const alignBox = (isParty: boolean) => {
      dialogueEl.classList.toggle('nb-align-npc', !isParty);
      dialogueEl.classList.toggle('nb-align-party', isParty);
    };
    alignBox(first.speaker.isParty);

    // Highlight specific party member if party is speaking
    const highlightSpeaker = (unitId?: string) => {
      mainMenu.querySelectorAll('.party-member.nb-speaking').forEach(el => el.classList.remove('nb-speaking'));
      if (unitId) {
        mainMenu.querySelector(`.party-member[data-unit-id="${unitId}"]`)?.classList.add('nb-speaking');
      }
    };
    if (first.speaker.isParty) highlightSpeaker(first.speaker.unitId);

    const showContinue = () => continueEl.classList.add('is-visible');

    /** Pre-measure full text height so the box stays stable during typewriter. */
    const preMeasure = (text: string) => {
      textEl.style.visibility = 'hidden';
      textEl.textContent = text;
      textEl.style.minHeight = `${textEl.scrollHeight}px`;
      textEl.textContent = '';
    };

    // Typewriter
    const typewrite = (text: string) => {
      // Kill any in-flight typewriter before starting a new one
      if (twTimer) { clearTimeout(twTimer); twTimer = null; }
      preMeasure(text);
      textEl.style.visibility = '';  // safe now — textContent is empty after preMeasure
      twFull = text;
      twTyping = true;
      textEl.textContent = '';
      continueEl.classList.remove('is-visible');
      let ci = 0;
      const tick = () => {
        if (ci < text.length) {
          textEl.textContent = text.substring(0, ++ci);
          twTimer = window.setTimeout(tick, SPEED);
        } else {
          twTyping = false;
          twTimer = null;
          showContinue();
        }
      };
      tick();
    };

    const completeTw = () => {
      if (twTimer) { clearTimeout(twTimer); twTimer = null; }
      textEl.textContent = twFull;
      twTyping = false;
      showContinue();
    };

    const teardown = () => {
      if (twTimer) clearTimeout(twTimer);
      stopVoiceover();
      document.removeEventListener('keydown', escListener);
      mainMenu.removeEventListener('click', sceneClick);
      mainMenu.classList.remove('nb-active', 'nb-speaker-npc', 'nb-speaker-party');
      mainMenu.querySelectorAll('.party-member.nb-speaking').forEach(el => el.classList.remove('nb-speaking'));
      // If we reused the quest NPC, restore it; otherwise remove the nb-npc.
      if (reuseQuestNpc) {
        const marker = questNpcEl!.querySelector('.quest-npc-marker') as HTMLElement | null;
        const nameLabel = questNpcEl!.querySelector('.quest-npc-name') as HTMLElement | null;
        if (marker) marker.style.visibility = '';
        if (nameLabel) nameLabel.style.visibility = '';
        questNpcEl!.style.pointerEvents = '';
      } else {
        npcEl.remove();
      }
      // Companion NPCs were rendered alongside the quest NPC for the briefing
      // — once the conversation ends they've served their purpose, so fade
      // them out and remove. The primary stays as the chapter's "click me".
      this.rootEl?.querySelectorAll('.quest-npc-co').forEach(el => {
        const coEl = el as HTMLElement;
        coEl.classList.remove('is-visible');
        window.setTimeout(() => coEl.remove(), 400);
      });
      dialogueEl.remove();
      if (featured) featured.style.display = '';
      if (secondary) secondary.style.display = '';
      this.briefingTeardown = null;
    };
    this.briefingTeardown = teardown;

    const cleanup = () => {
      teardown();
      if (crawlId) {
        // Persist seen state so a death + refresh doesn't replay the dialogue.
        try { localStorage.setItem(`briefing_seen_${crawlId}`, '1'); } catch { /* private mode */ }
        this.pendingCrawl = { crawlId, crawlType: 'campaign' };
        this.renderHero(); // Re-render hero card — CTA now starts the crawl
      } else {
        ScreenRouter.navigate('crawls');
      }
    };

    // Advance logic — shared by dialogue click and scene tap
    const advance = () => {
      if (twTyping) {
        completeTw();
        return;
      }

      pageIndex++;
      if (pageIndex >= dialogue.length) {
        cleanup();
        return;
      }

      const resolved = resolveSpeaker(briefing, dialogue[pageIndex]);
      const resolvedKey = resolved.speaker.spriteKey || dialogue[pageIndex].spriteKey || briefing.spriteKey || '';

      dialogueEl.classList.add('nb-text-fade');

      window.setTimeout(() => {
        // Portrait
        const newSrc = getPortraitUrl(resolvedKey);
        if (newSrc) portraitImg.src = newSrc;
        applyPortraitTuning(portraitImg, getPortraitTuning(resolvedKey));

        // Name / title
        nameEl.textContent = resolved.speaker.name;
        nameEl.classList.toggle('nb-name--party', resolved.speaker.isParty);
        titleEl.textContent = resolved.speaker.title;
        titleEl.style.display = resolved.speaker.title ? '' : 'none';

        // Highlight whichever NPC sprite is now speaking. Sprites live across
        // .quest-npc + .quest-npc-co siblings (when reusing quest NPC) or
        // inside .nb-npc (fallback path), so scope the search to the stage.
        // When the PARTY speaks, clear .is-speaking from every NPC so they
        // all dim equally — the previous NPC speaker would otherwise stay
        // bright while everyone else dimmed.
        const stage = npcEl.parentElement ?? npcEl;
        stage.querySelectorAll('.nb-npc-sprite').forEach(el => el.classList.remove('is-speaking'));
        if (!resolved.speaker.isParty) {
          const lineKey = dialogue[pageIndex].spriteKey || briefing.spriteKey || resolvedKey;
          stage.querySelector(`.nb-npc-sprite[data-sprite-key="${lineKey}"]`)?.classList.add('is-speaking');
        }

        // Speaker dimming + per-member highlighting + box alignment
        mainMenu.classList.toggle('nb-speaker-npc', !resolved.speaker.isParty);
        mainMenu.classList.toggle('nb-speaker-party', resolved.speaker.isParty);
        highlightSpeaker(resolved.speaker.isParty ? resolved.speaker.unitId : undefined);
        alignBox(resolved.speaker.isParty);

        // Continue label
        if (pageIndex === dialogue.length - 1) {
          continueEl.innerHTML = 'Tap to close &#9656;';
        }

        // Fade in + typewrite
        dialogueEl.classList.remove('nb-text-fade');
        if (audioBase) playVoiceover(audioBase, originalIndices[pageIndex], variantKeyForSpeaker(resolved.speaker, resolved.matchedTemplateId));
        typewrite(resolved.text);
      }, 120);
    };

    // Tap dialogue box to advance
    dialogueEl.addEventListener('click', advance);

    // Tap anywhere on the scene to advance (except interactive elements)
    const sceneClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.nb-dialogue, .screen-header, .menu-debug-toggle, .presence-bar, button, a')) return;
      advance();
    };
    mainMenu.addEventListener('click', sceneClick);

    // ESC to dismiss — teardown restores quest NPC marker/name automatically
    const escListener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') teardown();
    };
    document.addEventListener('keydown', escListener);

    // Animate in — only fade the dialogue box, NPC sprite stays as-is.
    // Reusing the quest NPC means the sprite is already in place (no fade).
    // Otherwise (fresh single or multi-NPC stage) we add `is-visible` so the
    // CSS opacity transition runs.
    requestAnimationFrame(() => {
      if (!reuseQuestNpc) npcEl.classList.add('is-visible');
      dialogueEl.classList.add('is-visible');
      // Start typewriter after entry animation settles
      setTimeout(() => {
        if (audioBase) playVoiceover(audioBase, originalIndices[0], variantKeyForSpeaker(first.speaker, first.matchedTemplateId));
        typewrite(first.text);
      }, 380);
    });
  }

  private buildDailyHeroConfig(crawlData: CrawlData): LocalHeroConfig {
    const zoneName = crawlData.config.name ?? 'Daily Crawl';
    const roomCount = crawlData.config.encounters?.length ?? 0;
    const isCompleted = crawlData.run?.status === 'completed';
    const isInProgress = crawlData.run?.status === 'in_progress';

    const ctaState: LocalHeroConfig['ctaState'] = isCompleted
      ? 'completed'
      : isInProgress
        ? 'in_progress'
        : 'available';
    const ctaLabel = isInProgress ? 'Resume' : 'Enter';

    return {
      variant: 'daily',
      // Bronze accent for daily.
      accentColor: '#c87a3a',
      bgKey: crawlData.config.bgKey,
      // No featured sprite for daily — let the bg take focus
      prebannerLabel: '\u2605 Daily Crawl \u2605',
      title: zoneName,
      meta: `${roomCount} Rooms \u00B7 Lv.${crawlData.config.dropItemLevel ?? '?'}+`,
      rewards: [],
      ctaLabel,
      ctaState,
      onCta: isCompleted ? undefined : () => {
        if (isInProgress) {
          this.startCrawl();
        } else {
          ScreenRouter.navigate('crawl-detail', { crawlId: crawlData.config.id, crawlType: 'daily' });
        }
      },
    };
  }

  // ----------------------------------------------------------------
  // Helpers
  // ----------------------------------------------------------------

  private isEventExpired(crawlData: CrawlData): boolean {
    const expiry = crawlData.config.eventExpiresAt;
    if (!expiry) return false;
    return new Date() > new Date(expiry + 'T23:59:59');
  }

  private buildExpiryHTML(crawlData: CrawlData): string {
    const expiresAt = crawlData.config.eventExpiresAt;
    if (!expiresAt) return '';
    const now = new Date();
    const end = new Date(expiresAt + 'T23:59:59');
    const daysLeft = Math.max(0, Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
    if (daysLeft <= 0) {
      return '<span class="event-banner-expiry expired">Event ended</span>';
    } else if (daysLeft === 1) {
      return '<span class="event-banner-expiry urgent">Ends tomorrow</span>';
    } else {
      return `<span class="event-banner-expiry">${daysLeft} days left</span>`;
    }
  }

  private async startEventCrawl(): Promise<void> {
    const btn = this.els.heroSlot?.querySelector('.hero-cta-btn') as HTMLButtonElement | null;
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Starting...';
    }

    const result = await APIManager.startEventCrawl();

    if (!result.success || !result.data) {
      console.error('[MainMenu] Failed to start event crawl:', result.error);
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'Enter Crawl';
      }
      return;
    }

    const { run, combatResult } = result.data;

    const eventCrawlData = StateManager.get('eventCrawlData') as CrawlData | undefined;
    if (eventCrawlData) {
      StateManager.set('eventCrawlData', { ...eventCrawlData, run });
      // Set crawlData too so CombatScreen picks up bgKey/zone/encounters
      StateManager.set('crawlData', { ...eventCrawlData, run });
    }

    ScreenRouter.replace('combat', { combatResult, run });
  }

  private async resetDailyCrawl(): Promise<void> {
    const result = await APIManager.resetDailyCrawl();
    if (!result.success) {
      console.error('[MainMenu] Failed to reset crawl:', result.error);
      return;
    }
    // Refresh crawl state
    const crawlResult = await APIManager.getTodaysCrawl();
    if (crawlResult.success && crawlResult.data) {
      StateManager.set('crawlData', crawlResult.data);
    }
  }

  private async adminGrant(type: 'item' | 'unit' | 'allStrands' | 'currencies' | 'memories' | 'skins' | 'unique' | 'allDyes'): Promise<void> {
    const methods = {
      item: () => APIManager.adminGrantItem(),
      unit: () => APIManager.adminGrantUnit(),
      allStrands: () => APIManager.adminGrantAllStrands(),
      currencies: () => APIManager.adminGrantCurrencies(),
      memories: () => APIManager.adminGrantMemories(),
      skins: () => APIManager.adminGrantSkins(),
      unique: () => APIManager.adminGrantUnique(),
      allDyes: () => APIManager.adminGrantAllDyes(),
    };

    const result = await methods[type]();
    if (!result.success) {
      console.error(`[MainMenu] Failed to grant ${type}:`, result.error);
      return;
    }

    // Refresh state so inventory/currencies update
    const stateResult = await APIManager.fetchUnifiedState();
    if (stateResult.success && stateResult.data) {
      StateManager.batchUpdate({
        playerData: stateResult.data.playerData,
        unitData: stateResult.data.unitData,
        itemData: stateResult.data.itemData,
      });
    }
  }

  private async adminSkipCampaignStep(): Promise<void> {
    const result = await APIManager.adminSkipCampaignStep();
    if (!result.success) {
      console.error('[MainMenu] Failed to skip campaign step:', result.error);
      return;
    }
    const campaignResult = await APIManager.getCampaign();
    if (campaignResult.success && campaignResult.data) {
      StateManager.set('campaignData', campaignResult.data);
    }
  }

  private async adminCompleteCampaignStep(): Promise<void> {
    const result = await APIManager.adminCompleteCampaignStep();
    if (!result.success) {
      console.error('[MainMenu] Failed to complete campaign step:', result.error);
      return;
    }
    // Rewards landed — refresh everything (player currencies, party XP, new items, new strands, campaign step)
    const stateResult = await APIManager.fetchUnifiedState();
    if (stateResult.success && stateResult.data) {
      StateManager.batchUpdate({
        playerData: stateResult.data.playerData,
        unitData: stateResult.data.unitData,
        itemData: stateResult.data.itemData,
        campaignData: stateResult.data.campaignData,
      });
    }
  }

  private async adminResetCampaign(): Promise<void> {
    const result = await APIManager.adminResetCampaign();
    if (!result.success) {
      console.error('[MainMenu] Failed to reset campaign:', result.error);
      return;
    }
    const campaignResult = await APIManager.getCampaign();
    if (campaignResult.success && campaignResult.data) {
      StateManager.set('campaignData', campaignResult.data);
    }
  }

  private async adminResetAccount(): Promise<void> {
    if (!confirm('Wipe this account back to the starter 3 strands? Items, gold, currencies, masteries, and campaign progress will be deleted.')) return;
    const result = await APIManager.adminResetAccount();
    if (!result.success) {
      console.error('[MainMenu] Failed to reset account:', result.error);
      return;
    }
    const stateResult = await APIManager.fetchUnifiedState();
    if (stateResult.success && stateResult.data) {
      StateManager.batchUpdate({
        playerData: stateResult.data.playerData,
        combatData: stateResult.data.combatData,
        crawlData: stateResult.data.crawlData,
        eventCrawlData: stateResult.data.eventCrawlData,
        campaignData: stateResult.data.campaignData,
        unitData: stateResult.data.unitData,
        itemData: stateResult.data.itemData,
        notificationData: stateResult.data.notificationData,
        coachData: stateResult.data.coachData,
      });
    }
  }

  private async adminKillAll(): Promise<void> {
    const result = await APIManager.debugKillAll();
    if (!result.success) {
      console.error('[MainMenu] Failed to kill all:', result.error);
      return;
    }
  }

  private adminResetBriefings(): void {
    try {
      const keys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('briefing_seen_')) keys.push(key);
      }
      keys.forEach(k => localStorage.removeItem(k));
    } catch { /* private mode */ }
    this.pendingCrawl = null;
    if (this.briefingTeardown) this.briefingTeardown();
    this.onRender();
  }

  private adminToggleAnchors(): void {
    setAutoAnchorsEnabled(!isAutoAnchorsEnabled());
    this.refreshAnchorsButton();
  }

  private refreshAnchorsButton(): void {
    const btn = this.$('.menu-toggle-anchors-btn');
    if (btn) btn.textContent = isAutoAnchorsEnabled() ? 'Anchors: AUTO' : 'Anchors: HAND';
  }

  private async adminGrantPattern(): Promise<void> {
    const result = await APIManager.adminGrantPattern('mine_caverns', 'rare');
    if (!result.success) {
      console.error('[MainMenu] Failed to grant pattern:', result.error);
      return;
    }
  }

  private async startCrawl(): Promise<void> {
    // Daily start can fire from either the hero slot (when daily is the lone hero)
    // or a secondary chip (when something else is the hero). Disable whichever button
    // is currently in the DOM.
    const btn = (this.els.heroSlot?.querySelector('.hero-cta-btn')
      ?? this.els.secondarySlot?.querySelector('.menu-enter-btn')) as HTMLButtonElement | null;
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Starting...';
    }

    const result = await APIManager.startCrawl();

    if (!result.success || !result.data) {
      console.error('[MainMenu] Failed to start crawl:', result.error);
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'Enter';
      }
      return;
    }

    const { run, combatResult } = result.data;

    // Update crawl state before navigating
    const crawlData = StateManager.get('crawlData') as CrawlData | undefined;
    if (crawlData) {
      StateManager.set('crawlData', { ...crawlData, run });
    }

    // Navigate to combat with the initial combat result
    ScreenRouter.replace('combat', { combatResult, run });
  }

  // ============================================================
  // QUEST NPC — standalone sprite on the hub with "!" marker
  // ============================================================

  private showQuestNpc(
    briefingData: { briefing: NpcBriefing },
    chapterTitle: string,
    crawlId?: string,
    stepIndex?: number,
  ): void {
    const stage = this.rootEl?.querySelector('.menu-stage') as HTMLElement | null;
    if (!stage) return;

    // Remove existing quest NPC and any companion sprites from prior chapters.
    stage.querySelector('.quest-npc')?.remove();
    stage.querySelectorAll('.quest-npc-co').forEach(el => el.remove());

    const primaryKey = briefingData.briefing.spriteKey || '';
    const spriteSrc = getFullSpriteUrl(primaryKey);
    const npcLine = briefingData.briefing.dialogue.find(d => d.speaker === 'npc');
    const npcName = npcLine?.name ?? 'Quest';
    const npcTitle = npcLine?.title ?? '';

    // Distinct NPC sprite keys in this briefing — when 2+, render the extras
    // as sibling sprites alongside the quest NPC so the home page shows the
    // full conversation cast and clicking doesn't shift anyone's position.
    const allNpcKeys = collectNpcSpriteKeys(briefingData.briefing, briefingData.briefing.dialogue);
    const companionKeys = allNpcKeys.filter(k => k !== primaryKey);

    const npcEl = document.createElement('div');
    npcEl.className = 'quest-npc';
    // Per-sprite scale (lanky Vael > stocky Granocen, etc.) applied inline so
    // it composes with the CSS scaleX(-1) flip. transform-origin: bottom center
    // keeps feet planted and grows the sprite upward.
    const primaryScale = PLAYER_SPRITES[primaryKey]?.scale ?? 1;
    // Expose the scale to .quest-npc-marker so it can compute its clearance —
    // taller scales need more gap above the head.
    npcEl.style.setProperty('--npc-scale', String(primaryScale));
    const primaryStyle = primaryScale !== 1
      ? `transform: scaleX(-1) scale(${primaryScale}); transform-origin: bottom center;`
      : '';
    // Reuse the party nameplate classes so the two labels share one style source.
    // Tag the primary <img> with nb-npc-sprite + data-sprite-key so the briefing
    // flow can toggle .is-speaking without rebuilding the stage.
    npcEl.innerHTML = `
      <div class="quest-npc-marker">!</div>
      ${spriteSrc ? `<img class="nb-npc-sprite is-speaking" data-sprite-key="${primaryKey}" src="${spriteSrc}" alt="${npcName}" draggable="false" style="${primaryStyle}" />` : ''}
      <div class="party-info quest-npc-name">
        <span class="party-name">${npcName}</span>
        ${npcTitle ? `<span class="party-element">${npcTitle}</span>` : ''}
      </div>
    `;
    stage.appendChild(npcEl);

    // Companion sprites — silent participants in the briefing, visible from
    // the home page so there's no "pop in" when the dialogue starts.
    companionKeys.forEach((key, i) => {
      const src = getFullSpriteUrl(key);
      if (!src) return;
      const plate = getNpcNameplate(briefingData.briefing, key);
      const coScale = PLAYER_SPRITES[key]?.scale ?? 1;
      const coStyle = coScale !== 1
        ? `transform: scaleX(-1) scale(${coScale}); transform-origin: bottom center;`
        : '';
      const coEl = document.createElement('div');
      coEl.className = 'quest-npc-co';
      coEl.style.setProperty('--co-index', String(i));
      coEl.innerHTML = `
        <img class="nb-npc-sprite" data-sprite-key="${key}" src="${src}" alt="${plate.name}" draggable="false" style="${coStyle}" />
        <div class="party-info quest-npc-name">
          <span class="party-name">${plate.name}</span>
          ${plate.title ? `<span class="party-element">${plate.title}</span>` : ''}
        </div>
      `;
      stage.appendChild(coEl);
      requestAnimationFrame(() => coEl.classList.add('is-visible'));
    });

    // Click to trigger briefing — pass the NPC element so it stays in place.
    // stopPropagation prevents this click from bubbling to the sceneClick
    // handler that showBriefingInline registers on mainMenu, which would
    // immediately advance() the dialogue before the typewriter even starts.
    npcEl.addEventListener('click', (e) => {
      e.stopPropagation();
      this.showBriefingInline(briefingData.briefing, chapterTitle, crawlId, npcEl, stepIndex);
    });

    // Animate in
    requestAnimationFrame(() => npcEl.classList.add('is-visible'));
  }

  // ============================================================
  // ROSTER PICKER — bottom sheet for swapping party members
  // ============================================================

  private showRosterPicker(slotIndex: number): void {
    this.swapSlotIndex = slotIndex;
    const unitData = StateManager.get('unitData') as UnitData | undefined;
    const configData = StateManager.get('configData') as ConfigData | undefined;
    if (!unitData) return;

    const party = unitData.party ?? [];
    const partyIds = new Set(party.map(u => u.id));
    const available = unitData.roster.filter(u => !partyIds.has(u.id));
    if (available.length === 0) {
      import('../components/Toast').then(({ ToastManager }) => {
        ToastManager.show({ type: 'info', message: 'All your strands are in your party' });
      });
      return;
    }

    RosterPicker.open({
      title: 'Select Strand',
      units: available,
      renderCard: (unit) => renderHomePickerCard(unit, configData),
      cardClasses: (unit) => {
        const hasSkills = SKILL_SLOT_KEYS.some(key => unit[key]);
        return hasSkills ? '' : 'roster-card--naked';
      },
      onPick: (unit) => this.swapPartyMember(unit.id),
      onClose: () => {
        this.els.partyShowcase?.querySelectorAll('.party-member--swapping')
          .forEach(el => el.classList.remove('party-member--swapping'));
      },
    });
  }

  private closeRosterPicker(): void {
    RosterPicker.close();
    this.els.partyShowcase?.querySelectorAll('.party-member--swapping')
      .forEach(el => el.classList.remove('party-member--swapping'));
  }

  private async swapPartyMember(newUnitId: string): Promise<void> {
    const unitData = StateManager.get('unitData') as UnitData | undefined;
    if (!unitData) return;

    const party = [...(unitData.party ?? [])];
    if (this.swapSlotIndex < 0 || this.swapSlotIndex >= party.length) return;

    // Build new party IDs with the swap
    const newIds = party.map((u, i) => i === this.swapSlotIndex ? newUnitId : u.id);

    this.closeRosterPicker();

    const result = await APIManager.equipParty(newIds);
    if (!result.success) {
      console.error('[MainMenu] Failed to swap party:', result.error);
      return;
    }

    // Refresh state to get updated party
    const stateResult = await APIManager.fetchUnifiedState();
    if (stateResult.success && stateResult.data) {
      StateManager.batchUpdate({
        unitData: stateResult.data.unitData,
        playerData: stateResult.data.playerData,
      });
    }
    this.renderParty();
  }

  private async beginCrawl(): Promise<void> {
    if (this.startingCrawl || !this.pendingCrawl) return;
    this.startingCrawl = true;

    const unitData = StateManager.get('unitData') as UnitData | undefined;
    const party = unitData?.party ?? [];
    const unitIds = party.map(u => u.id);

    if (unitIds.length !== 3) {
      console.error('[MainMenu] Need 3 party members');
      this.startingCrawl = false;
      return;
    }

    // Disable the hero card CTA
    const btn = this.els.heroSlot?.querySelector('.hero-cta-btn') as HTMLButtonElement | null;
    if (btn) { btn.disabled = true; btn.textContent = 'Starting...'; }

    const { crawlId, crawlType } = this.pendingCrawl;

    let result;
    if (crawlType === 'daily') {
      result = await APIManager.startCrawl(unitIds);
    } else if (crawlType === 'event') {
      result = await APIManager.startEventCrawl(unitIds);
    } else if (crawlType === 'campaign') {
      result = await APIManager.startCampaignRun(unitIds);
    } else if (crawlType === 'campaign_replay') {
      result = await APIManager.startCampaignReplay(crawlId, unitIds);
    } else {
      result = await APIManager.startFreeCrawl(unitIds);
    }

    if (!result.success || !result.data) {
      console.error('[MainMenu] Failed to start crawl:', result.error);
      this.startingCrawl = false;
      if (btn) { btn.disabled = false; btn.textContent = 'Enter'; }
      return;
    }

    const { run, combatResult } = result.data;

    // Fetch unified state so CombatScreen gets full crawlData (bgKey, lighting, etc.)
    const stateResult = await APIManager.fetchUnifiedState();
    if (stateResult.success && stateResult.data) {
      StateManager.batchUpdate({
        crawlData: stateResult.data.crawlData,
        campaignData: stateResult.data.campaignData,
        unitData: stateResult.data.unitData,
        playerData: stateResult.data.playerData,
      });
    }

    this.pendingCrawl = null;
    this.startingCrawl = false;

    ScreenRouter.replace('combat', { combatResult, run });
  }

  /** Test helper: grant 1000 gold and wipe the player's shop listings so the
   *  next shop fetch regenerates a fresh set. Used for repeated buy testing. */
  private async adminRefreshShop(): Promise<void> {
    const result = await APIManager.adminRefreshShop();
    if (!result.success) {
      console.error('[MainMenu] Failed to refresh shop:', result.error);
      return;
    }
    // Refresh state so the new gold balance shows up immediately. The shop
    // itself regenerates lazily on the next /api/shop fetch when the player
    // navigates to the shop screen.
    const stateResult = await APIManager.fetchUnifiedState();
    if (stateResult.success && stateResult.data) {
      StateManager.batchUpdate({
        playerData: stateResult.data.playerData,
      });
    }
  }
}

// ----------------------------------------------------------------
// Local types — extends shared HeroBannerConfig with an onCta callback that
// MainMenuScreen wires up after the helper renders the HTML.
// ----------------------------------------------------------------

type LocalHeroConfig = HeroBannerConfig & { onCta?: () => void };
